import React, { useState, useEffect } from 'react';
import AreaSearchModal from './AreaSearchModal';
import RemoteFilterDropdown from './RemoteFilterModal';
import SkillsFilterDropdown from './SkillsFilterModal';
import OccupationsFilterDropdown from './OccupationsFilterModal';
import IndustriesFilterDropdown from './IndustriesFilterModal';
import WorkDaysFilterDropdown from './WorkDaysFilterModal';
import CharacteristicsFilterDropdown from './CharacteristicsFilterModal';

interface MobileSearchModalProps {
  show: boolean;
  onClose: () => void;
  onSearch: (filters: {
    keyword: string;
    prefecture_ids: number[];
    price_over?: number;
    is_recruiting: boolean;
    remote_types: string[];
    skill_ids: number[];
    occupation_ids: number[];
    industry_ids: number[];
    workday_types: string[];
    characteristic_ids: number[];
  }) => void;
  initialFilters: {
    keyword: string;
    prefecture_ids: number[];
    price_over?: number;
    is_recruiting: boolean;
    remote_types: string[];
    skill_ids: number[];
    occupation_ids: number[];
    industry_ids: number[];
    workday_types: string[];
    characteristic_ids: number[];
  };
}

const MobileSearchModal: React.FC<MobileSearchModalProps> = ({
  show,
  onClose,
  onSearch,
  initialFilters
}) => {
  const [inputValue, setInputValue] = useState(initialFilters.keyword);
  const [selectedPrefectureIds, setSelectedPrefectureIds] = useState<number[]>(initialFilters.prefecture_ids);
  const [selectedPriceOver, setSelectedPriceOver] = useState<number | undefined>(initialFilters.price_over);
  const [recruitingOnly, setRecruitingOnly] = useState(initialFilters.is_recruiting);
  const [selectedRemoteTypes, setSelectedRemoteTypes] = useState<string[]>(initialFilters.remote_types);
  const [selectedSkillIds, setSelectedSkillIds] = useState<number[]>(initialFilters.skill_ids);
  const [selectedOccupationIds, setSelectedOccupationIds] = useState<number[]>(initialFilters.occupation_ids);
  const [selectedIndustryIds, setSelectedIndustryIds] = useState<number[]>(initialFilters.industry_ids);
  const [selectedWorkdayTypes, setSelectedWorkdayTypes] = useState<string[]>(initialFilters.workday_types);
  const [selectedCharacteristicIds, setSelectedCharacteristicIds] = useState<number[]>(initialFilters.characteristic_ids);
  const [selectedCharacteristicNames, setSelectedCharacteristicNames] = useState<string[]>([]);
  const [isLoadingCharacteristics, setIsLoadingCharacteristics] = useState(false);
  
  const [showAreaModal, setShowAreaModal] = useState(false);
  const [showRemoteDropdown, setShowRemoteDropdown] = useState(false);
  const [showSkillsDropdown, setShowSkillsDropdown] = useState(false);
  const [showOccupationsDropdown, setShowOccupationsDropdown] = useState(false);
  const [showIndustriesDropdown, setShowIndustriesDropdown] = useState(false);
  const [showWorkdaysDropdown, setShowWorkdaysDropdown] = useState(false);
  const [showCharacteristicsDropdown, setShowCharacteristicsDropdown] = useState(false);

  // 初期値の同期
  useEffect(() => {
    setInputValue(initialFilters.keyword);
    setSelectedPrefectureIds(initialFilters.prefecture_ids);
    setSelectedPriceOver(initialFilters.price_over);
    setRecruitingOnly(initialFilters.is_recruiting);
    setSelectedRemoteTypes(initialFilters.remote_types);
    setSelectedSkillIds(initialFilters.skill_ids);
    setSelectedOccupationIds(initialFilters.occupation_ids);
    setSelectedIndustryIds(initialFilters.industry_ids);
    setSelectedWorkdayTypes(initialFilters.workday_types);
    setSelectedCharacteristicIds(initialFilters.characteristic_ids);
  }, [initialFilters]);

  // 特徴の名前を取得
  useEffect(() => {
    if (selectedCharacteristicIds.length > 0) {
      setIsLoadingCharacteristics(true);
      fetch('/api/v1/characteristics')
        .then(response => response.json())
        .then(data => {
          const names = selectedCharacteristicIds.map(id => {
            const characteristic = data.find((c: any) => c.id === id);
            return characteristic ? characteristic.name : '';
          }).filter(name => name);
          setSelectedCharacteristicNames(names);
          setIsLoadingCharacteristics(false);
        })
        .catch(error => {
          console.error('Failed to fetch characteristic names:', error);
          setIsLoadingCharacteristics(false);
        });
    } else {
      setSelectedCharacteristicNames([]);
    }
  }, [selectedCharacteristicIds]);

  const handleSearch = () => {
    onSearch({
      keyword: inputValue,
      prefecture_ids: selectedPrefectureIds,
      price_over: selectedPriceOver,
      is_recruiting: recruitingOnly,
      remote_types: selectedRemoteTypes,
      skill_ids: selectedSkillIds,
      occupation_ids: selectedOccupationIds,
      industry_ids: selectedIndustryIds,
      workday_types: selectedWorkdayTypes,
      characteristic_ids: selectedCharacteristicIds
    });
    onClose();
  };

  const handleClear = () => {
    setInputValue('');
    setSelectedPrefectureIds([]);
    setSelectedPriceOver(undefined);
    setRecruitingOnly(false);
    setSelectedRemoteTypes([]);
    setSelectedSkillIds([]);
    setSelectedOccupationIds([]);
    setSelectedIndustryIds([]);
    setSelectedWorkdayTypes([]);
    setSelectedCharacteristicIds([]);
    setSelectedCharacteristicNames([]);
  };

  const handleAreaApply = (prefIds: number[]) => {
    setSelectedPrefectureIds(prefIds);
  };

  const handleRemoteApply = (remoteTypes: string[]) => {
    setSelectedRemoteTypes(remoteTypes);
  };

  const handleSkillsApply = (skillIds: number[]) => {
    setSelectedSkillIds(skillIds);
  };

  const handleOccupationsApply = (occupationIds: number[]) => {
    setSelectedOccupationIds(occupationIds);
  };

  const handleIndustriesApply = (industryIds: number[]) => {
    setSelectedIndustryIds(industryIds);
  };

  const handleWorkdaysApply = (workdayTypes: string[]) => {
    setSelectedWorkdayTypes(workdayTypes);
  };

  const handleCharacteristicsApply = (characteristicIds: number[]) => {
    setSelectedCharacteristicIds(characteristicIds);
    
    // 特徴の名前を取得
    if (characteristicIds.length > 0) {
      setIsLoadingCharacteristics(true);
      fetch('/api/v1/characteristics')
        .then(response => response.json())
        .then(data => {
          const names = characteristicIds.map(id => {
            const characteristic = data.find((c: any) => c.id === id);
            return characteristic ? characteristic.name : '';
          }).filter(name => name);
          setSelectedCharacteristicNames(names);
          setIsLoadingCharacteristics(false);
        })
        .catch(error => {
          console.error('Failed to fetch characteristic names:', error);
          setIsLoadingCharacteristics(false);
        });
    } else {
      setSelectedCharacteristicNames([]);
    }
  };

  // 価格帯オプション（8段階）
  const priceOptions = [
    { value: '', label: '単価' },
    { value: '300000', label: '30万円～' },
    { value: '400000', label: '40万円～' },
    { value: '500000', label: '50万円～' },
    { value: '600000', label: '60万円～' },
    { value: '700000', label: '70万円～' },
    { value: '800000', label: '80万円～' },
    { value: '900000', label: '90万円～' },
    { value: '1000000', label: '100万円～' }
  ];

  if (!show) {
    console.log('MobileSearchModal: show is false, not rendering');
    return null;
  }

  console.log('MobileSearchModal: rendering modal, show =', show);

  return (
    <>
      <div 
        className="modal fade show d-block"
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          zIndex: 1060,
          backgroundColor: 'rgba(0,0,0,0.5)'
        }}
        onClick={(e) => {
          // バックドロップをクリックした時のみ閉じる
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div 
          className="modal-dialog modal-fullscreen"
          style={{ margin: 0, maxWidth: 'none', height: '100%', zIndex: 1061 }}
          onClick={(e) => {
            // モーダルコンテンツ内のクリックは伝播させない
            e.stopPropagation();
          }}
        >
          <div className="modal-content" style={{ height: '100%', border: 'none', borderRadius: 0 }}>
            {/* ヘッダー */}
            <div className="modal-header bg-primary text-white">
              <h5 className="modal-title fw-bold">検索条件</h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={onClose}
              ></button>
            </div>

            {/* ボディ - スクロール可能 */}
            <div className="modal-body p-4" style={{ overflowY: 'auto', height: 'calc(100% - 120px)' }}>
              <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
                {/* キーワード検索 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">キーワード</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="キーワードで検索"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                    />
                  </div>
                </div>

                {/* エリア選択 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">勤務地</label>
                  <button
                    type="button"
                    className="form-control text-start"
                    onClick={() => {
                      console.log('Area button clicked, opening AreaSearchModal');
                      setShowAreaModal(true);
                    }}
                    style={{ 
                      backgroundColor: '#f8f9fa', 
                      borderColor: '#6c757d', 
                      color: '#495057',
                      border: '1px solid #6c757d',
                      cursor: 'pointer'
                    }}
                  >
                    {selectedPrefectureIds.length > 0 
                      ? `選択済み (${selectedPrefectureIds.length})` 
                      : 'エリアを選択'
                    }
                  </button>
                </div>

                {/* 価格下限 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">単価</label>
                  <select
                    className="form-select"
                    value={selectedPriceOver || ''}
                    onChange={(e) => setSelectedPriceOver(e.target.value ? Number(e.target.value) : undefined)}
                  >
                    {priceOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 募集中 */}
                <div className="mb-4">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="recruitingOnly"
                      checked={recruitingOnly}
                      onChange={(e) => setRecruitingOnly(e.target.checked)}
                    />
                    <label className="form-check-label fw-bold" htmlFor="recruitingOnly">
                      募集中のみ
                    </label>
                  </div>
                </div>

                {/* 勤務形態 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">勤務形態</label>
                  <div className="position-relative">
                    <button
                      type="button"
                      className="form-control text-start"
                      onClick={() => setShowRemoteDropdown(!showRemoteDropdown)}
                      style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderColor: '#6c757d', 
                        color: '#495057',
                        border: '1px solid #6c757d',
                        cursor: 'pointer'
                      }}
                    >
                      {selectedRemoteTypes.length > 0 
                        ? `選択済み (${selectedRemoteTypes.length})` 
                        : '勤務形態を選択'
                      }
                    </button>
                    <RemoteFilterDropdown
                      show={showRemoteDropdown}
                      onClose={() => setShowRemoteDropdown(false)}
                      onApply={handleRemoteApply}
                      selectedRemoteTypes={selectedRemoteTypes}
                      buttonRef={null}
                    />
                  </div>
                </div>

                {/* スキル */}
                <div className="mb-4">
                  <label className="form-label fw-bold">スキル</label>
                  <div className="position-relative">
                    <button
                      type="button"
                      className="form-control text-start"
                      onClick={() => setShowSkillsDropdown(!showSkillsDropdown)}
                      style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderColor: '#6c757d', 
                        color: '#495057',
                        border: '1px solid #6c757d',
                        cursor: 'pointer'
                      }}
                    >
                      {selectedSkillIds.length > 0 
                        ? `選択済み (${selectedSkillIds.length})` 
                        : 'スキルを選択'
                      }
                    </button>
                    <SkillsFilterDropdown
                      show={showSkillsDropdown}
                      onHide={() => setShowSkillsDropdown(false)}
                      onApply={handleSkillsApply}
                      selectedSkillIds={selectedSkillIds}
                      buttonRef={null}
                    />
                  </div>
                </div>

                {/* 職種 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">職種</label>
                  <div className="position-relative">
                    <button
                      type="button"
                      className="form-control text-start"
                      onClick={() => setShowOccupationsDropdown(!showOccupationsDropdown)}
                      style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderColor: '#6c757d', 
                        color: '#495057',
                        border: '1px solid #6c757d',
                        cursor: 'pointer'
                      }}
                    >
                      {selectedOccupationIds.length > 0 
                        ? `選択済み (${selectedOccupationIds.length})` 
                        : '職種を選択'
                      }
                    </button>
                    <OccupationsFilterDropdown
                      show={showOccupationsDropdown}
                      onHide={() => setShowOccupationsDropdown(false)}
                      onApply={handleOccupationsApply}
                      selectedOccupationIds={selectedOccupationIds}
                      buttonRef={null}
                    />
                  </div>
                </div>

                {/* 業界 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">業界</label>
                  <div className="position-relative">
                    <button
                      type="button"
                      className="form-control text-start"
                      onClick={() => setShowIndustriesDropdown(!showIndustriesDropdown)}
                      style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderColor: '#6c757d', 
                        color: '#495057',
                        border: '1px solid #6c757d',
                        cursor: 'pointer'
                      }}
                    >
                      {selectedIndustryIds.length > 0 
                        ? `選択済み (${selectedIndustryIds.length})` 
                        : '業界を選択'
                      }
                    </button>
                    <IndustriesFilterDropdown
                      show={showIndustriesDropdown}
                      onClose={() => setShowIndustriesDropdown(false)}
                      onApply={handleIndustriesApply}
                      selectedIndustryIds={selectedIndustryIds}
                      buttonRef={null}
                    />
                  </div>
                </div>

                {/* 週日数 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">週日数</label>
                  <div className="position-relative">
                    <button
                      type="button"
                      className="form-control text-start"
                      onClick={() => setShowWorkdaysDropdown(!showWorkdaysDropdown)}
                      style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderColor: '#6c757d', 
                        color: '#495057',
                        border: '1px solid #6c757d',
                        cursor: 'pointer'
                      }}
                    >
                      {selectedWorkdayTypes.length > 0 
                        ? `選択済み (${selectedWorkdayTypes.length})` 
                        : '週日数を選択'
                      }
                    </button>
                    <WorkDaysFilterDropdown
                      show={showWorkdaysDropdown}
                      onClose={() => setShowWorkdaysDropdown(false)}
                      onApply={handleWorkdaysApply}
                      selectedWorkdayTypes={selectedWorkdayTypes}
                      buttonRef={null}
                    />
                  </div>
                </div>

                {/* 特徴 */}
                <div className="mb-4">
                  <label className="form-label fw-bold">特徴</label>
                  <div className="position-relative">
                    <button
                      type="button"
                      className="form-control text-start"
                      onClick={() => setShowCharacteristicsDropdown(!showCharacteristicsDropdown)}
                      style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderColor: '#6c757d', 
                        color: '#495057',
                        border: '1px solid #6c757d',
                        cursor: 'pointer'
                      }}
                    >
                      {selectedCharacteristicIds.length > 0 
                        ? `選択済み (${selectedCharacteristicIds.length})` 
                        : '特徴を選択'
                      }
                    </button>
                    <CharacteristicsFilterDropdown
                      show={showCharacteristicsDropdown}
                      onClose={() => setShowCharacteristicsDropdown(false)}
                      onApply={handleCharacteristicsApply}
                      selectedCharacteristicIds={selectedCharacteristicIds}
                      buttonRef={null}
                    />
                    
                    {/* 選択された特徴の表示 */}
                    {selectedCharacteristicIds.length > 0 && (
                      <div className="mt-2">
                        {isLoadingCharacteristics ? (
                          <div className="d-flex align-items-center">
                            <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                              <span className="visually-hidden">読み込み中...</span>
                            </div>
                            <span className="text-muted small">特徴を読み込み中...</span>
                          </div>
                        ) : (
                          <div className="d-flex flex-wrap gap-1">
                            {selectedCharacteristicNames.map((name, index) => (
                              <span key={index} className="badge bg-secondary">
                                {name}
                                <button
                                  type="button"
                                  className="btn-close btn-close-white ms-1"
                                  style={{ fontSize: '0.6em' }}
                                  onClick={() => {
                                    const newIds = selectedCharacteristicIds.filter((_, i) => i !== index);
                                    const newNames = selectedCharacteristicNames.filter((_, i) => i !== index);
                                    setSelectedCharacteristicIds(newIds);
                                    setSelectedCharacteristicNames(newNames);
                                    handleCharacteristicsApply(newIds);
                                  }}
                                ></button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>

            {/* フッター */}
            <div className="modal-footer bg-light border-top">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleClear}
              >
                条件をクリア
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSearch}
              >
                <i className="bi bi-search me-1"></i>
                検索する
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* モーダル群 */}
      <AreaSearchModal
        show={showAreaModal}
        selectedPrefectureIds={selectedPrefectureIds}
        onClose={() => setShowAreaModal(false)}
        onApply={handleAreaApply}
        onImmediateSearch={handleAreaApply}
      />
    </>
  );
};

export default MobileSearchModal;
